<template>
  <div class="pipeline-timeline">
    <div
        v-for="(step, index) in displaySteps"
        :key="step.key"
        class="pipeline-timeline__step"
    >
      <span class="pipeline-timeline__dot" :class="`pipeline-timeline__dot--${step.state}`">
        <span v-if="step.state === 'active'" class="pipeline-status-spinner" aria-hidden="true"></span>
        <SvgIcon v-else-if="dotIcon(step.state)" :name="dotIcon(step.state)" class="w-3 h-3" />
      </span>
      <p class="pipeline-timeline__label" :class="`pipeline-timeline__label--${step.state}`">
        {{ $t(`notifications.timeline.${step.key}`) }}<template v-if="step.word">: {{ step.word }}</template>
      </p>
      <span
          v-if="index < displaySteps.length - 1"
          class="pipeline-timeline__connector"
          :class="{ 'pipeline-timeline__connector--done': step.state === 'done' }"
      ></span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';
import { stepDisplayState, stepWord } from '@/utils/pipelineTimeline';

const props = defineProps({
  steps: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();

// Mirrors statusLabel()'s fallback in store/Notification.js.
const translateStatus = (rawStatus) => {
  const key = `pipeline-status.${rawStatus}`;
  const translated = t(key);
  return translated === key ? rawStatus : translated;
};

// Steps are rendered from this collapsed shape so the template never has to
// deal with the 'segments' branch itself - no per-environment chip list here
// anymore, just the one folded word, same as every other step.
const displaySteps = computed(() => props.steps.map((step) => ({
  key: step.key,
  state: stepDisplayState(step),
  word: stepWord(step, translateStatus),
})));

const DOT_ICONS = {
  done: 'check-circle',
  failed: 'alert-circle',
  stale: 'clock',
  skipped: 'ban',
};

const dotIcon = (state) => DOT_ICONS[state] || null;
</script>

<style scoped>
.pipeline-timeline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.875rem 0;
}

.pipeline-timeline__step {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.pipeline-timeline__dot {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid var(--color-grey-500);
  color: var(--color-grey-500);
}

.pipeline-timeline__dot--done {
  border-color: var(--color-success);
  color: var(--color-success);
}

.pipeline-timeline__dot--active {
  border-color: var(--color-info);
  color: var(--color-info);
}

.pipeline-timeline__dot--failed {
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.pipeline-timeline__dot--stale {
  border-color: var(--color-warning);
  color: var(--color-warning);
}

.pipeline-timeline__dot--skipped {
  opacity: 0.5;
}

.pipeline-timeline__label {
  font-size: 0.8rem;
  color: var(--color-grey-700);
  white-space: nowrap;
}

.pipeline-timeline__label--done {
  color: var(--color-success);
  font-weight: 600;
}

.pipeline-timeline__label--active {
  color: var(--color-info);
  font-weight: 600;
}

.pipeline-timeline__label--failed {
  color: var(--color-danger);
  font-weight: 600;
}

.pipeline-timeline__connector {
  height: 2px;
  width: clamp(2rem, 10vw, 6rem);
  margin: 0 0.5rem;
  background-color: var(--color-grey-500);
}

.pipeline-timeline__connector--done {
  background-color: var(--color-success);
}

.pipeline-status-spinner {
  display: inline-block;
  width: 0.75rem;
  height: 0.75rem;
  border: 2px solid var(--color-grey-100);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: pipeline-timeline-spin 1s linear infinite;
}

@keyframes pipeline-timeline-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
